import TypesenseInstantSearchAdapter from 'typesense-instantsearch-adapter'
import { algoliaFilterToTypesense } from './algoliaFilterToTypesense'
import {
  COLLECTIONS,
  CONNECTION_TIMEOUT_SECONDS,
  DEFAULT_INDEX_NAMES,
  LISTING_EXCLUDE_FIELDS,
  PRESETS,
  SEARCH_CACHE_SECONDS,
  createIndexResolver,
  curationTags,
  stripHitExtras,
  type AlgoliaIndexNames,
  type TypesenseSearchConfig
} from './typesense'

// On its own module so that only the listing pages (category, search) load the adapter (mobiilinopeus 27.10.2026)

/** Marker passed through the adapter's "<collection>/sort/<sort_by>" index name, swapped for the sort preset */
const SORT_PRESET_MARKER = 'preset='

/**
 * Search client for vue-instantsearch, built on typesense-instantsearch-adapter.
 * Maps Algolia index names (incl. sort replicas) and Algolia `filters` before handing requests to the adapter.
 */
export function createTypesenseInstantSearchClient(config: TypesenseSearchConfig, algoliaIndexes: AlgoliaIndexNames = DEFAULT_INDEX_NAMES) {
  const resolveIndex = createIndexResolver(algoliaIndexes)

  const adapter = new TypesenseInstantSearchAdapter({
    server: {
      ...(config.nearestNode && { nearestNode: { ...config.nearestNode } }),
      nodes: config.nodes.map(node => ({ ...node })),
      apiKey: config.apiKey,
      connectionTimeoutSeconds: CONNECTION_TIMEOUT_SECONDS,
      cacheSearchResultsForSeconds: SEARCH_CACHE_SECONDS
    },
    additionalSearchParameters: {
      preset: PRESETS.products
    },
    collectionSpecificSearchParameters: {
      [COLLECTIONS.products]: {
        preset: PRESETS.products,
        group_by: 'parentSku',
        group_limit: 1,
        group_missing_values: false,
        exclude_fields: LISTING_EXCLUDE_FIELDS.products
      },
      [COLLECTIONS.categories]: { preset: PRESETS.categories },
      [COLLECTIONS.contents]: { preset: PRESETS.contents },
      [COLLECTIONS.blogs]: { preset: PRESETS.blogs }
    }
  } as any)

  // The adapter only knows sort_by. A sort replica is passed as a marker in sort_by and turned into the sort preset here.
  const typesenseClient = (adapter as any).typesenseClient
  const multiSearch = typesenseClient.multiSearch.perform.bind(typesenseClient.multiSearch)
  typesenseClient.multiSearch.perform = (request: { searches: Record<string, unknown>[] }, commonParams: unknown) => multiSearch({
    ...request,
    searches: request.searches.map((search) => {
      const sortBy = search.sort_by as string | undefined
      if (!sortBy?.startsWith(SORT_PRESET_MARKER)) return search
      const { sort_by: _sortBy, ...rest } = search
      return { ...rest, preset: sortBy.slice(SORT_PRESET_MARKER.length) }
    })
  }, commonParams)

  const baseClient = adapter.searchClient as any

  const mapRequest = (request: any) => {
    const target = resolveIndex(request.indexName)
    const isSortPreset = target.preset !== PRESETS[target.kind]
    return {
      ...request,
      indexName: isSortPreset ? `${target.collection}/sort/${SORT_PRESET_MARKER}${target.preset}` : target.collection,
      params: {
        ...request.params,
        filters: algoliaFilterToTypesense(request.params?.filters),
        ruleContexts: curationTags(request.params?.ruleContexts)
      }
    }
  }

  return {
    ...baseClient,
    search: async (requests: any[]) => {
      const response = await baseClient.search(requests.map(mapRequest))
      response.results?.forEach((result: any, i: number) => stripHitExtras(result?.hits, requests[i]?.params))
      return response
    },
    searchForFacetValues: (requests: any[]) => baseClient.searchForFacetValues(requests.map(mapRequest))
  }
}
